/**
 * Token Usage Card Component
 *
 * Full card displaying comprehensive token usage statistics with:
 * - Circular progress indicator
 * - Used/Budget counts
 * - Status badge
 * - Time until reset
 * - API remaining tokens (if available)
 *
 * Auto-refreshes every 60 seconds when visible.
 */

import { useEffect } from 'react';
import { TokenUsageProgress } from './TokenUsageProgress';
import { useTokenUsageStore } from '../../../shared/store/tokenUsageStore';

interface TokenUsageCardProps {
  onSyncClick?: () => void;
}

const statusBadgeStyles = {
  NORMAL: 'bg-green-100 text-green-800',
  CAUTION: 'bg-yellow-100 text-yellow-800',
  WARNING: 'bg-orange-100 text-orange-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

/**
 * Token usage card with auto-refresh
 *
 * @example
 * ```tsx
 * <TokenUsageCard onSyncClick={() => console.log('Sync clicked')} />
 * ```
 */
export function TokenUsageCard({ onSyncClick }: TokenUsageCardProps) {
  const { status, isLoading, error, fetchStatus } = useTokenUsageStore();

  useEffect(() => {
    // Fetch immediately on mount
    fetchStatus();

    // Refresh every 60 seconds when visible
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Loading skeleton
  if (isLoading && !status) {
    return (
      <div className="p-4 bg-dark-surface rounded-lg animate-pulse">
        <div className="h-20 bg-dark-surface/50 rounded" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 bg-red-900/20 rounded-lg text-red-400">
        <div className="font-semibold mb-1">Failed to load token usage</div>
        <div className="text-sm">{error}</div>
      </div>
    );
  }

  // No data (shouldn't happen after loading completes)
  if (!status) return null;

  return (
    <div className="p-4 bg-dark-surface rounded-lg">
      <div className="flex items-center gap-4">
        {/* Circular progress indicator */}
        <TokenUsageProgress
          used={status.used}
          budget={status.budget}
          status={status.status}
          size="md"
        />

        {/* Stats and metadata */}
        <div className="flex-1">
          {/* Token counts */}
          <div className="text-lg font-semibold text-white">
            {status.used.toLocaleString()} / {status.budget.toLocaleString()}
            <span className="text-sm font-normal text-dark-muted ml-1">tokens</span>
          </div>

          {/* Status badge and reset time */}
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeStyles[status.status]}`}>
              {status.status}
            </span>
            <span className="text-sm text-dark-muted">
              Resets in: {status.time_until_reset}
            </span>
          </div>

          {/* API actual remaining (if synced) */}
          {status.api_actual_remaining !== null && (
            <div className="text-xs text-dark-muted mt-1">
              API reports: {status.api_actual_remaining.toLocaleString()} remaining
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
